import {
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  BoltIcon,
  EyeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { ROUTES } from '../../../utils/routes';
import { hasActiveSubscription } from '../../../utils/subscriptions';
import {
  FOLLOW_UP_TEMPLATE,
  FOLLOW_UP_TEMPLATE_DESCRIPTIONS,
  FOLLOW_UP_TEMPLATE_LABELS,
} from '../../Prospecting/OwnerCommunication/utils/emailContentGenerator';
import type { ContentChoiceHandle } from '../../Setup/ContentChoice/ContentChoice.module';
import ContentChoice from '../../Setup/ContentChoice/ContentChoice.module';
import type { PreviewTarget } from '../../Setup/ContentChoice/PreviewModal';
import PreviewModal from '../../Setup/ContentChoice/PreviewModal';
import type { FramedSectionVisual } from './FramedSection';
import FramedSection from './FramedSection';
import SubscriptionBanner from './SubscriptionBanner';

type ConsultationCardBase = {
  title: string;
  description: string;
  ctaLabel: string;
  badgeLabel: string;
  icon: React.ElementType;
  ctaIcon: React.ElementType;
};

type ConsultationCardConfig =
  | (ConsultationCardBase & { ctaRoute: string; onCtaClick?: never })
  | (ConsultationCardBase & { onCtaClick: () => void; ctaRoute?: never });

const ACTION_VISUAL: FramedSectionVisual = {
  wrapperClass: 'border-orange-100 bg-gradient-to-br from-orange-50/60 via-white to-white',
  accentBarClass: 'bg-gradient-to-b from-orange-500 to-orange-600',
  iconClass: 'bg-orange-100 text-orange-600',
  icon: <BoltIcon className="h-5 w-5" />,
  badgeClass: 'bg-orange-100 text-orange-800 ring-1 ring-orange-200',
  badgeIcon: <BoltIcon className="h-3 w-3" />,
  badgeLabel: 'ACTION REQUISE',
  subtitleClass: 'text-rgpd-blue',
};

const CONSULTATION_VISUAL: FramedSectionVisual = {
  wrapperClass: 'border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white',
  accentBarClass: 'bg-gradient-to-b from-slate-400 to-slate-500',
  iconClass: 'bg-slate-100 text-slate-500',
  icon: <InformationCircleIcon className="h-5 w-5" />,
  badgeClass: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
  badgeIcon: <EyeIcon className="h-3 w-3" />,
  badgeLabel: 'POUR INFORMATION',
  subtitleClass: 'text-slate-500',
};

function ConsultationCard({ card }: { card: ConsultationCardConfig }) {
  const Icon = card.icon;
  const CtaIcon = card.ctaIcon;
  const ctaContent = (
    <>
      <CtaIcon className="h-4 w-4" />
      {card.ctaLabel}
      <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
    </>
  );
  const ctaClass = 'inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white py-2.5 text-[13px] font-bold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50';

  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-md">
      <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/80 px-5 py-3.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-slate-600 ring-1 ring-slate-200">
          <Icon className="h-4.5 w-4.5" />
        </div>
        <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[10.5px] font-bold tracking-wide text-slate-600 ring-1 ring-slate-200">
          {card.badgeLabel.toUpperCase()}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-2.5 px-5 py-4">
        <h4 className="text-[14px] leading-snug font-bold text-slate-800">{card.title}</h4>
        <p className="text-[12.5px] leading-relaxed text-gray-600">{card.description}</p>
      </div>

      <div className="border-t border-slate-100 px-5 py-3.5">
        {card.ctaRoute ? (
          <Link to={card.ctaRoute} className={ctaClass}>
            {ctaContent}
          </Link>
        ) : (
          <button type="button" onClick={card.onCtaClick} className={ctaClass}>
            {ctaContent}
          </button>
        )}
      </div>
    </div>
  );
}

function ParametrageContenuTab() {
  const navigate = useNavigate();
  const contentChoiceRef = useRef<ContentChoiceHandle>(null);
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null);
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const isLocked = !hasActiveSubscription(company);

  const handleOpenBlogPage = async() => {
    const ok = await contentChoiceRef.current?.saveIfDirty();
    if (ok === false) {return;}
    navigate(ROUTES.COMMUNICATION_PAGE_SITE);
  };

  const consultationCards: ConsultationCardConfig[] = [
    {
      icon: ArrowPathIcon,
      title: `DPE — ${FOLLOW_UP_TEMPLATE_LABELS[FOLLOW_UP_TEMPLATE.STANDARD]} après contact initial`,
      description: FOLLOW_UP_TEMPLATE_DESCRIPTIONS[FOLLOW_UP_TEMPLATE.STANDARD],
      badgeLabel: 'Modèle figé',
      ctaLabel: 'Voir un exemple',
      ctaIcon: EyeIcon,
      onCtaClick: () => setPreviewTarget({ kind: 'dpe-follow-up' }),
    },
    {
      icon: IdentificationIcon,
      title: 'Mail de confirmation transmission de coordonnées gestionnaire/transactionnaire',
      description: 'Mail envoyé au copropriétaire après son rendez-vous téléphonique pour formaliser le consentement recueilli et confirmer la transmission de ses coordonnées au transactionnaire ou au gestionnaire désigné.',
      badgeLabel: 'Modèle figé',
      ctaLabel: 'Voir un exemple',
      ctaIcon: EyeIcon,
      onCtaClick: () => setPreviewTarget({ kind: 'consent-after-call' }),
    },
    {
      icon: GlobeAltIcon,
      title: 'Lien vers le blog des articles à inclure sur votre site',
      description: 'Récupérez le bouton HTML personnalisé à intégrer sur votre site pour rediriger vos visiteurs vers votre blog COPRAVIE.',
      badgeLabel: 'Page dédiée',
      ctaLabel: 'Ouvrir la page',
      ctaIcon: ArrowTopRightOnSquareIcon,
      onCtaClick: () => { void handleOpenBlogPage(); },
    },
  ];

  return (
    <div>
      <div className="relative">
        <div className={classNames('flex flex-col gap-8', isLocked && 'pointer-events-none select-none blur-[2px]')}>
          <FramedSection
            visual={ACTION_VISUAL}
            title="Contenu croissance"
            subtitle="Personnalisez les modèles de communication par défaut. Vos choix sont utilisés automatiquement comme base pour vos suivis propriétaires et vos campagnes Référent — vous gardez bien sûr la main sur chaque envoi."
          >
            <ContentChoice ref={contentChoiceRef} />
          </FramedSection>

          <div className="relative flex items-center" aria-hidden="true">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="mx-3 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-[10.5px] font-bold tracking-wide text-slate-500">
              <EyeIcon className="h-3 w-3" />
              CONSULTATION UNIQUEMENT
            </span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <FramedSection
            visual={CONSULTATION_VISUAL}
            title="Contenu à consulter uniquement"
            subtitle="Ces contenus sont fournis tels quels — il n'y a rien à choisir ici. Consultez-les pour vous familiariser avec ce que reçoivent vos copropriétaires ou pour les intégrer à votre communication."
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {consultationCards.map((card) => (
                <ConsultationCard key={card.title} card={card} />
              ))}
            </div>
          </FramedSection>
        </div>

        {isLocked && (
          <div className="absolute inset-0 z-20 flex items-start justify-center">
            <div className="mt-32 flex max-w-[80%] flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white px-8 py-6 text-center shadow-lg">
              <p className="text-sm font-bold text-rgpd-blue-dark">Contenu réservé au Module Croissance</p>
              <p className="text-sm text-gray-500">
                La personnalisation de vos contenus sera débloquée une fois votre abonnement au Module Croissance activé.
              </p>
            </div>
          </div>
        )}
      </div>

      <PreviewModal
        isOpen={previewTarget !== null}
        target={previewTarget}
        onClose={() => setPreviewTarget(null)}
      />

      <div className="mt-8">
        <SubscriptionBanner company={company} jwt={jwt} />
      </div>
    </div>
  );
}

export default ParametrageContenuTab;
